import { useMutation, useQuery } from "@tanstack/react-query";
import { Value } from "@wojtekmaj/react-daterange-picker/dist/cjs/shared/types";
import { toastServerError } from "helpers";
import {
  COMPANY_DELIVERIES_LABELS,
  downloadAllDriversDeliveriesReport,
  downloadOneDriverDeliveriesReport
} from "modules/financial";

import {
  requestCompanyDriverDeliveries,
  requestCompanyFinanceDrivers,
  requestCompanyFinanceReport
} from "./companyFinance.requests";
import {
  CompanyDriverDeliveriesParams,
  CompanyFinanceParams
} from "./companyFinance.types";

export const useCompanyFinanceDrivers = (
  companyId: string,
  params: CompanyFinanceParams
) => {
  const { data, ...rest } = useQuery({
    queryKey: ["company", companyId, "finance", "drivers", params],
    queryFn: () => requestCompanyFinanceDrivers(companyId, params),
    enabled: !!companyId,
    onError: toastServerError
  });

  return { drivers: data, ...rest };
};

/** Fetched on click only: the whole period is printed, never rendered. */
export const useCompanyFinanceReport = (
  companyId: string,
  params: CompanyFinanceParams,
  dateRange: Value
) =>
  useMutation({
    mutationFn: () => requestCompanyFinanceReport(companyId, params),
    onSuccess: (items) =>
      downloadAllDriversDeliveriesReport(
        items,
        dateRange,
        COMPANY_DELIVERIES_LABELS
      ),
    onError: toastServerError
  });

export const useCompanyDriverDeliveries = (
  companyId: string,
  params: CompanyDriverDeliveriesParams
) => {
  const { data, ...rest } = useQuery({
    queryKey: ["company", companyId, "finance", "driver-deliveries", params],
    queryFn: () => requestCompanyDriverDeliveries(companyId, params),
    enabled: !!companyId && !!params.driverId,
    keepPreviousData: true,
    onError: toastServerError
  });

  const allPagesDeliveries = useMutation({
    mutationFn: () =>
      requestCompanyDriverDeliveries(companyId, { ...params, take: 2000 }),
    onSuccess: (response) =>
      downloadOneDriverDeliveriesReport({
        driver: response.data.driver,
        deliveries: response.data.deliveries,
        dateRange: { from: params.from, to: params.to },
        labels: COMPANY_DELIVERIES_LABELS
      }),
    onError: toastServerError
  });

  return {
    driver: data?.data.driver,
    deliveries: data?.data.deliveries,
    meta: data?.meta,
    allPagesDeliveries,
    ...rest
  };
};
